import { type ReactNode, useCallback, useMemo, useState } from 'react';

// Shared admin UI kit. Mirrors the web app's components/ui so the back-office
// gets the same care (one Logo, one button style, consistent panels/toasts).

/** Compact relative time, e.g. "3m", "5h", "12d", "—" for null. */
export function timeAgo(ms: number | null | undefined): string {
  if (!ms) return '—';
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30);
  return mo < 12 ? `${mo}mo` : `${Math.floor(mo / 12)}y`;
}

export type SortDir = 'asc' | 'desc';

/** Client-side table sort (null-safe, numeric/natural). Mirrors the web hook. */
export function useSort<T>(rows: T[], initial?: { key: string; dir?: SortDir }) {
  const [sortKey, setSortKey] = useState<string | null>(initial?.key ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(initial?.dir ?? 'desc');
  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const get = (r: T) => (r as Record<string, unknown>)[sortKey];
    return [...rows].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      let c: number;
      if (av == null && bv == null) c = 0;
      else if (av == null) c = -1;
      else if (bv == null) c = 1;
      else if (typeof av === 'number' && typeof bv === 'number') c = av - bv;
      else c = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === 'asc' ? c : -c;
    });
  }, [rows, sortKey, sortDir]);
  const toggle = (k: string) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir('desc');
    }
  };
  return { sorted, sortKey, sortDir, toggle };
}

/** A clickable, sortable `<th>` for admin tables. */
export function SortHeader({
  label,
  k,
  sortKey,
  sortDir,
  onSort,
  className = 'py-1',
}: {
  label: ReactNode;
  k: string;
  sortKey: string | null;
  sortDir: SortDir;
  onSort: (k: string) => void;
  className?: string;
}) {
  const active = sortKey === k;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className="inline-flex items-center gap-1 font-medium transition hover:text-neutral-800"
      >
        {label}
        <span
          className={`text-[9px] leading-none ${active ? 'text-emerald-600' : 'text-neutral-300'}`}
        >
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

export const input =
  'w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20';
export const btn =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50';
export const btnGhost =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-neutral-300 px-3.5 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50';

export function LogoMark({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="#0B0F0E" />
      <rect x="7" y="8" width="18" height="4.5" rx="2.25" fill="#34D399" />
      <rect x="7" y="13.75" width="13" height="4.5" rx="2.25" fill="#FBBF24" />
      <rect x="7" y="19.5" width="8" height="4.5" rx="2.25" fill="#FB7185" />
    </svg>
  );
}

export function TabIcon({ path }: { path: string }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={path}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// --- Icon set (single source of truth for grid action glyphs) ----------------
// Lucide-style 24x24 stroke icons. Action columns use these instead of words
// ("Inspect", "Details", "Remove", …) — each IconButton keeps an aria-label/title.
export type IconName =
  | 'view'
  | 'expand'
  | 'collapse'
  | 'retry'
  | 'rescore'
  | 'approve'
  | 'token'
  | 'revoke'
  | 'remove'
  | 'edit'
  | 'save'
  | 'database'
  | 'mail'
  | 'history'
  | 'prev'
  | 'next';

const ICON_GLYPH: Record<IconName, ReactNode> = {
  view: (
    <>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  expand: <path d="m6 9 6 6 6-6" />,
  collapse: <path d="m18 15-6-6-6 6" />,
  retry: (
    <>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 21v-5h5" />
    </>
  ),
  rescore: (
    <>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  approve: <path d="M20 6 9 17l-5-5" />,
  token: (
    <>
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="m10.7 12.3 9.3-9.3" />
      <path d="m16 5 3 3" />
      <path d="m13 8 3 3" />
    </>
  ),
  revoke: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m5.6 5.6 12.8 12.8" />
    </>
  ),
  remove: (
    <>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </>
  ),
  edit: (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </>
  ),
  save: (
    <>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <path d="M17 21v-8H7v8" />
      <path d="M7 3v5h8" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14a9 3 0 0 0 18 0V5" />
      <path d="M3 12a9 3 0 0 0 18 0" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </>
  ),
  history: (
    <>
      <path d="M3 3v5h5" />
      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
      <path d="M12 7v5l4 2" />
    </>
  ),
  prev: <path d="m15 18-6-6 6-6" />,
  next: <path d="m9 18 6-6-6-6" />,
};

export function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_GLYPH[name]}
    </svg>
  );
}

const ICON_TONE: Record<Exclude<Tone, never>, string> = {
  emerald: 'text-emerald-600 hover:bg-emerald-50',
  sky: 'text-sky-600 hover:bg-sky-50',
  indigo: 'text-indigo-600 hover:bg-indigo-50',
  rose: 'text-rose-600 hover:bg-rose-50',
  amber: 'text-amber-600 hover:bg-amber-50',
  neutral: 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900',
};

/** Compact icon-only action button. `label` is required (title + aria-label). */
export function IconButton({
  icon,
  label,
  onClick,
  disabled,
  tone = 'neutral',
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: Tone;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent ${ICON_TONE[tone]}`}
    >
      <Icon name={icon} />
    </button>
  );
}

/**
 * Pager footer for data grids. Works for both server-side pages (pass `total`)
 * and client-side slices (omit `total`). `count` is the number of rows on the
 * current page.
 */
export function Pager({
  offset,
  count,
  total,
  hasMore,
  onPrev,
  onNext,
}: {
  offset: number;
  count: number;
  total?: number;
  hasMore: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (offset === 0 && !hasMore) return null; // single page — no chrome needed
  const from = count === 0 ? 0 : offset + 1;
  const to = offset + count;
  return (
    <div className="mt-3 flex items-center justify-between border-t border-neutral-100 pt-2 text-xs text-neutral-500">
      <span>
        {from}–{to}
        {total != null ? ` of ${total.toLocaleString()}` : ''}
      </span>
      <div className="flex items-center gap-1">
        <IconButton icon="prev" label="Previous page" onClick={onPrev} disabled={offset === 0} />
        <IconButton icon="next" label="Next page" onClick={onNext} disabled={!hasMore} />
      </div>
    </div>
  );
}

export function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-6 rounded-2xl border border-neutral-200/80 bg-white p-5 shadow-sm ring-1 ring-black/[0.02]">
      <h2 className="mb-4 font-semibold">{title}</h2>
      {/* Wide content (tables, grids) scrolls horizontally on small screens
          instead of blowing out the page width. */}
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}

export type Tone = 'emerald' | 'sky' | 'indigo' | 'rose' | 'amber' | 'neutral';

const TONE_TEXT: Record<Tone, string> = {
  emerald: 'text-emerald-600',
  sky: 'text-sky-600',
  indigo: 'text-indigo-600',
  rose: 'text-rose-600',
  amber: 'text-amber-600',
  neutral: 'text-neutral-900',
};
const TONE_DOT: Record<Tone, string> = {
  emerald: 'bg-emerald-500',
  sky: 'bg-sky-500',
  indigo: 'bg-indigo-500',
  rose: 'bg-rose-500',
  amber: 'bg-amber-400',
  neutral: 'bg-neutral-300',
};

/** Flat metric tile (no gradient): a labelled number with a small tone accent. */
export function Metric({
  label,
  value,
  tone = 'neutral',
  hint,
}: { label: string; value: number | string; tone?: Tone; hint?: string }) {
  return (
    // min-w-0 lets the tile shrink inside a grid so a long number can't blow the
    // row out; the value scales down on small screens and clips rather than overrun.
    <div className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white p-4">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-neutral-500">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONE_DOT[tone]}`} />
        <span className="truncate">{label}</span>
      </div>
      <div
        className={`mt-1 truncate text-2xl font-semibold tabular-nums tracking-tight sm:text-3xl ${TONE_TEXT[tone]}`}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {hint && <div className="mt-0.5 truncate text-xs text-neutral-400">{hint}</div>}
    </div>
  );
}

const BADGE_TONE: Record<Tone, string> = {
  emerald: 'bg-emerald-100 text-emerald-700',
  sky: 'bg-sky-100 text-sky-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  rose: 'bg-rose-100 text-rose-700',
  amber: 'bg-amber-100 text-amber-800',
  neutral: 'bg-neutral-100 text-neutral-600',
};

/** Small status pill. */
export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${BADGE_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

export function jobStatusTone(s: string): Tone {
  return s === 'completed'
    ? 'emerald'
    : s === 'failed'
      ? 'rose'
      : s === 'processing'
        ? 'sky'
        : 'amber';
}
export function accountStatusTone(s: string): Tone {
  return s === 'active' ? 'emerald' : s === 'pending' ? 'amber' : 'rose';
}

/** Inline error with a retry — shown when a data load fails. */
export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
      <span>{message}</span>
      {onRetry && (
        <button
          type="button"
          className="font-medium underline hover:no-underline"
          onClick={onRetry}
        >
          Retry
        </button>
      )}
    </div>
  );
}

export type Toast = { msg: string; tone: 'error' | 'success' };
export type Notify = (msg: string, tone?: 'error' | 'success') => void;

export function Toaster({ toast }: { toast: Toast | null }) {
  if (!toast) return null;
  return (
    <div
      className={`fixed bottom-5 right-5 z-50 max-w-sm rounded-lg px-4 py-3 text-sm font-medium shadow-lg ring-1 ${
        toast.tone === 'error'
          ? 'bg-rose-600 text-white ring-rose-700'
          : 'bg-emerald-600 text-white ring-emerald-700'
      }`}
      aria-live="polite"
    >
      {toast.msg}
    </div>
  );
}

/**
 * Wrap a mutation so failures are never silent: optionally confirm first, then
 * run, surfacing a success or error toast. Returns false if cancelled/failed.
 */
export function useRun(notify: Notify) {
  return useCallback(
    async (fn: () => Promise<unknown>, opts?: { confirm?: string; success?: string }) => {
      if (opts?.confirm && !window.confirm(opts.confirm)) return false;
      try {
        await fn();
        if (opts?.success) notify(opts.success, 'success');
        return true;
      } catch (e) {
        notify(e instanceof Error ? e.message : 'Action failed', 'error');
        return false;
      }
    },
    [notify],
  );
}
